import { useEffect, useRef, useState } from "react";
import { ZoomIn, X } from "lucide-react";
import type { GalleryItem } from "@/lib/shopify";

/** Append a Shopify CDN transform param to an image URL. Non-Shopify URLs are returned as-is. */
function shopifyImg(url: string, width: number) {
  if (!url || !url.includes("cdn.shopify.com")) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}width=${width}`;
}
function shopifySrcSet(url: string, widths: number[]) {
  if (!url || !url.includes("cdn.shopify.com")) return undefined;
  return widths.map((w) => `${shopifyImg(url, w)} ${w}w`).join(", ");
}

export function ProductGallery({
  items,
  title,
  activeImageUrl,
}: {
  items: GalleryItem[];
  title: string;
  activeImageUrl?: string | null;
}) {
  const [idx, setIdx] = useState(0);
  const hasItems = items.length > 0;
  const slots = hasItems ? items : Array.from({ length: 5 }, () => null);

  useEffect(() => {
    if (!activeImageUrl || !hasItems) return;
    const found = items.findIndex((item) => item.url === activeImageUrl);
    if (found >= 0 && found !== idx) setIdx(found);
  }, [activeImageUrl, items, hasItems]);

  const active = hasItems ? items[idx] : null;

  const imgRef = useRef<HTMLDivElement>(null);
  const [hoverZoom, setHoverZoom] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [lightbox, setLightbox] = useState(false);

  const isImage = active && active.kind !== "video";
  const zoomUrl = active ? shopifyImg(active.url, 2400) : "";

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setLightbox(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <div className="space-y-3">
      <div
        ref={imgRef}
        className="aspect-square bg-muted overflow-hidden relative group"
        onMouseEnter={() => isImage && setHoverZoom(true)}
        onMouseLeave={() => setHoverZoom(false)}
        onMouseMove={isImage ? onMove : undefined}
      >
        {active ? (
          active.kind === "video" ? (
            <video
              key={active.url}
              src={active.url}
              poster={active.poster ? shopifyImg(active.poster, 1200) : undefined}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              width={800}
              height={800}
              aria-label={active.altText || title}
              className="w-full h-full object-cover"
            />
          ) : (
            <>
              <img
                src={shopifyImg(active.url, 1200)}
                srcSet={shopifySrcSet(active.url, [600, 900, 1200, 1600])}
                sizes="(min-width: 768px) 50vw, 100vw"
                alt={active.altText || title}
                width={800}
                height={800}
                loading={idx === 0 ? "eager" : "lazy"}
                fetchPriority={idx === 0 ? "high" : "auto"}
                decoding="async"
                className={`w-full h-full object-cover transition-opacity duration-150 ${hoverZoom ? "opacity-0" : "opacity-100"}`}
              />
              {hoverZoom && (
                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: `url(${zoomUrl})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "250%",
                    backgroundPosition: `${pos.x}% ${pos.y}%`,
                  }}
                />
              )}
              <button
                type="button"
                onClick={() => setLightbox(true)}
                aria-label="Open zoom viewer"
                className="absolute bottom-3 right-3 z-10 w-10 h-10 flex items-center justify-center bg-background/80 backdrop-blur border border-border text-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </>
          )
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-muted to-muted-foreground/10">
            <span className="font-serif text-3xl text-muted-foreground/60">
              {String(idx + 1).padStart(2, "0")}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              Image placeholder
            </span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-5 gap-2">
        {slots.map((item, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={item ? (item.kind === "video" ? `View video ${i + 1}` : `View image ${i + 1}`) : `View placeholder ${i + 1}`}
            className={`aspect-square overflow-hidden border relative ${
              i === idx ? "border-foreground" : "border-border"
            }`}
          >
            {item ? (
              item.kind === "video" ? (
                <video
                  src={item.url}
                  poster={item.poster ? shopifyImg(item.poster, 240) : undefined}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  width={240}
                  height={240}
                  aria-hidden="true"
                  tabIndex={-1}
                  className="w-full h-full object-cover pointer-events-none"
                />
              ) : (
                <img
                  src={shopifyImg(item.url, 240)}
                  alt=""
                  width={240}
                  height={240}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                {String(i + 1).padStart(2, "0")}
              </div>
            )}
          </button>
        ))}
      </div>
      {lightbox && isImage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed product image"
          className="fixed inset-0 z-50 bg-background/95 backdrop-blur flex items-center justify-center p-4 overflow-auto"
          onClick={() => setLightbox(false)}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            aria-label="Close zoom viewer"
            className="fixed top-4 right-4 w-10 h-10 flex items-center justify-center border border-border bg-background text-foreground hover:bg-muted"
          >
            <X className="w-4 h-4" />
          </button>
          <img
            src={zoomUrl}
            alt={active?.altText || title}
            className="max-w-none cursor-zoom-out"
            style={{ width: "min(2400px, 180vw)", height: "auto" }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

